import React, { useState, useMemo } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import { formatToDecimals } from '@lib/helpers/formatting';
import { columnToRight } from '@lib/helpers/agGridHelpers';
import OESimpleTable from '@components/oetable/OESimpleTable';
import LineChart from 'components/charts/LineChart';
import { SectionFrame, SectionTitle, NumberFrame } from 'components/atomic/atoms';
import ExpenseItemCard from './ExpenseItemCard';
import ExpenseItemHistogram from 'components/charts/ExpenseItemHistogram';
import { CompleteFinancialProgress, CashedInProgress } from '@lib/api_ts/progress';
import Select from 'react-select';
//import LineChart from 'components/charts/lineChart';

type DataPointProps = {
	amount: number;
	finalAmount: number;
	cashedAmount: number;
	cashedInAmount: number;
	residual?: number;
};

const columnDefsTotalsByYears = [
	{
		headerName: 'Anno',
		field: 'year',
		filter: 'agTextColumnFilter',
		minWidth: 120,
	},
	{
		headerName: 'Spesa prevista (€)',
		field: 'amount',
		filter: 'agTextColumnFilter',
		valueFormatter: (params) => {
			return formatToDecimals(params.data.amount);
		},
		filterValueGetter: (params) => {
			return formatToDecimals(params.data.amount);
		},
		valueGetter: (params) => {
			return params.data.amount > 0 ? params.data.amount : 0;
		},
		minWidth: 250,
		maxWidth: 250,
		...columnToRight(),
	},
	{
		headerName: 'Spesa effettuata (€)',
		field: 'finalAmount',
		filter: 'agTextColumnFilter',
		valueFormatter: (params) => {
			return formatToDecimals(params.data.finalAmount);
		},
		filterValueGetter: (params) => {
			return formatToDecimals(params.data.finalAmount);
		},
		valueGetter: (params) => {
			return params.data.finalAmount > 0 ? params.data.finalAmount : 0;
		},
		minWidth: 250,
		maxWidth: 250,
		...columnToRight(),
	},
	{
		headerName: 'Spesa residua (€)',
		field: 'accumulatedResidual',
		filter: 'agTextColumnFilter',
		valueFormatter: (params) => {
			return formatToDecimals(params.data.accumulatedResidual);
		},
		filterValueGetter: (params) => {
			return formatToDecimals(params.data.accumulatedResidual);
		},
		valueGetter: (params) => {
			return params.data.accumulatedResidual !== 0 ? params.data.accumulatedResidual : 0;
		},
		minWidth: 250,
		maxWidth: 250,
		...columnToRight(),
	},
	{
		headerName: 'Incasso previsto (€)',
		field: 'cashedAmount',
		filter: 'agTextColumnFilter',
		valueFormatter: (params) => {
			return formatToDecimals(params.data.cashedAmount);
		},
		filterValueGetter: (params) => {
			return formatToDecimals(params.data.cashedAmount);
		},
		valueGetter: (params) => {
			return params.data.cashedAmount > 0 ? params.data.cashedAmount : 0;
		},
		minWidth: 250,
		maxWidth: 250,
		...columnToRight(),
	},
	{
		headerName: 'Incasso effettivo (€)',
		field: 'cashedIn',
		filter: 'agTextColumnFilter',
		valueFormatter: (params) => {
			return formatToDecimals(params.data.cashedIn);
		},
		filterValueGetter: (params) => {
			return formatToDecimals(params.data.cashedIn);
		},
		valueGetter: (params) => {
			return params.data.cashedIn > 0 ? params.data.cashedIn : 0;
		},
		minWidth: 250,
		maxWidth: 250,
		...columnToRight(),
	},
];

const FinancialTotalsTab = ({ financialAmounts, cashedInData }: { financialAmounts: CompleteFinancialProgress[], cashedInData: CashedInProgress[] }) => {
	const [selectedYear, setSelectedYear] = useState<string | number>();

	const totalsByYears: (DataPointProps & { year: number })[] = useMemo(() => {
		const aggregatedYears: Record<number, DataPointProps> = {};
		financialAmounts.forEach((finAmount) => {
			if (aggregatedYears[finAmount.year]) {
				aggregatedYears[finAmount.year] = {
					amount: aggregatedYears[finAmount.year].amount + finAmount.amount,
					finalAmount: aggregatedYears[finAmount.year].finalAmount + finAmount.finalAmount,
					cashedAmount: aggregatedYears[finAmount.year].cashedAmount + finAmount.cashedAmount,
					cashedInAmount: aggregatedYears[finAmount.year].cashedInAmount + finAmount.cashedAmount,
				};
			} else {
				aggregatedYears[finAmount.year] = {
					amount: finAmount.amount,
					finalAmount: finAmount.finalAmount,
					cashedAmount: finAmount.cashedAmount,
					cashedInAmount: finAmount.cashedInAmount,
				};
			}
		});

		const yearsArray = Object.keys(aggregatedYears)
			.map(Number)
			.sort((a, b) => a - b)
			.map((year) => ({
				year,
				...aggregatedYears[year],
				residual: aggregatedYears[year].amount - aggregatedYears[year].finalAmount,
			}));
		return yearsArray;
	}, [financialAmounts]);

	const yearOptions = useMemo(
		() => totalsByYears?.map((yearData) => ({ value: yearData.year.toString(), label: yearData.year.toString() })),
		[totalsByYears],
	);

	const yearDataPoints = useMemo(
		() => ({
			amount: totalsByYears.map((row) => ({ x: row.year, y: row.amount })),
			finalAmount: totalsByYears.map((row) => ({ x: row.year, y: row.finalAmount })),
			cashedAmount: totalsByYears.map((row) => ({ x: row.year, y: row.cashedAmount })),
			cashedInAmount: totalsByYears.map((row) => ({ x: row.year, y: row.cashedInAmount })),
			residual: totalsByYears.map((row) => ({ x: row.year, y: row.residual ?? 0 })),
		}),
		[totalsByYears],
	);

	const totalsByMonth: Record<number, DataPointProps> = useMemo(() => {
		const selectedAmounts = financialAmounts?.filter((amount) => amount.year === selectedYear);
		const aggregatedMonths: Record<number, DataPointProps> = {};

		selectedAmounts.forEach((selectedAmount) => {
			if (aggregatedMonths[selectedAmount.month]) {
				aggregatedMonths[selectedAmount.month] = {
					amount: aggregatedMonths[selectedAmount.month].amount + selectedAmount.amount,
					finalAmount: aggregatedMonths[selectedAmount.month].finalAmount + selectedAmount.finalAmount,
					cashedAmount: aggregatedMonths[selectedAmount.month].cashedAmount + selectedAmount.cashedAmount,
					cashedInAmount: aggregatedMonths[selectedAmount.month].cashedInAmount,
				};
			} else {
				aggregatedMonths[selectedAmount.month] = {
					amount: selectedAmount.amount,
					finalAmount: selectedAmount.finalAmount,
					cashedAmount: selectedAmount.cashedAmount,
					cashedInAmount: selectedAmount.cashedInAmount,
				};
			}
		});
		return aggregatedMonths;
	}, [financialAmounts, selectedYear]);

	const monthDataPoints = useMemo(
		() => ({
			amount: Object.keys(totalsByMonth).map((row) => ({ x: parseInt(row), y: totalsByMonth[row].amount })),
			finalAmount: Object.keys(totalsByMonth).map((row) => ({ x: parseInt(row), y: totalsByMonth[row].finalAmount })),
			cashedAmount: Object.keys(totalsByMonth).map((row) => ({ x: parseInt(row), y: totalsByMonth[row].cashedAmount })),
			cashedInAmount: Object.keys(totalsByMonth).map((row) => ({ x: parseInt(row), y: totalsByMonth[row].cashedInAmount })),
		}),
		[totalsByMonth],
	);

	/* const test = [
      { year: 2022, amount: 10, finalAmount: 5, residual: 5 },    //0
      { year: 2023, amount: 20, finalAmount: 15, residual: 5 },   //0
      { year: 2024, amount: 30, finalAmount: 50, residual: -20 }, //0
      { year: 2025, amount: 50, finalAmount: 30, residual: 20 },  //5+5-20+20= 10
      { year: 2026, amount: 60, finalAmount: 0, residual: 60 },   //60
      { year: 2027, amount: 70, finalAmount: 0, residual: 70 },   //70
   ]         //totali       240             100           140 */

	const processTotalsByYears = (inputTotals: (DataPointProps & { year: number })[], cashedIn: CashedInProgress[]) => {
		const cashedInByYear: Record<number, number> = {};

		cashedIn?.forEach((item) => {
			cashedInByYear[item.year] = (cashedInByYear[item.year] || 0) + item.cashedInAmount;
		});

		const allYears = new Set([
			...inputTotals.map((t) => t.year),
			...Object.keys(cashedInByYear).map(Number),
		]);

		const sortedYears = Array.from(allYears).sort((a, b) => a - b);
		const minYear = sortedYears[0];
		const maxYear = sortedYears[sortedYears.length - 1];
		const filledYears: number[] = [];

		if (minYear != null && maxYear != null) {
			for (let y = minYear; y <= maxYear; y++) {
				filledYears.push(y);
			}
		}

		const mergedTotals = filledYears
			.map((year) => {
				const existing = inputTotals.find((t) => t.year === year);
				return {
					year,
					amount: existing?.amount ?? 0,
					finalAmount: existing?.finalAmount ?? 0,
					residual: existing?.residual ?? 0,
					cashedAmount: existing?.cashedAmount ?? 0,
					cashedIn: cashedInByYear[year] ?? 0,
				};
			});

		let accumulatedResidual = 0;

		return mergedTotals.map((yearData, index) => {
			accumulatedResidual += yearData.residual;

			return {
				...yearData,
				accumulatedResidual: index === mergedTotals.length - 1 ? accumulatedResidual : 0,
			};
		});
	};

	const totalsByExpenseItems: { voceDiSpesa: string; sottovoceDiSpesa: string; amount: number; finalAmount: number; residual: number }[] =
		useMemo(() => {
			const tempTotals = {};
			financialAmounts.forEach((finAmount) => {
				const { amount, finalAmount, voceDiSpesa, sottovoceDiSpesa, projectExpenseItemId } = finAmount;
				const currentValue = tempTotals[projectExpenseItemId];
				if (currentValue) {
					tempTotals[projectExpenseItemId] = {
						voceDiSpesa,
						sottovoceDiSpesa,
						amount: currentValue.amount + amount,
						finalAmount: currentValue.finalAmount + finalAmount,
						residual: currentValue.amount + amount - (currentValue.finalAmount + finalAmount),
					};
				} else {
					tempTotals[finAmount.projectExpenseItemId] = {
						voceDiSpesa,
						sottovoceDiSpesa,
						amount,
						finalAmount,
						residual: amount - finalAmount,
					};
				}
			});
			return Object.keys(tempTotals)
				.map((totalKey) => ({
					...tempTotals[totalKey],
					projectExpenseItemId: totalKey,
				}))
				.filter((total) => total.amount !== 0);
		}, [financialAmounts]);

	return (
		<>
			<Row className="mt-4 mb-5">
				<Col>
					<SectionFrame>
						<div className="row">
							<SectionTitle>Monitoraggio spesa per anno</SectionTitle>
						</div>
						<div className="row gap-4 mb-5">
							<NumberFrame
								label="Totale spesa prevista"
								value={formatToDecimals(yearDataPoints.amount?.reduce((acc, cur) => acc + cur.y, 0)) + ' €'}
							/>
							<NumberFrame
								label="Totale spesa effettuata"
								value={formatToDecimals(yearDataPoints.finalAmount?.reduce((acc, cur) => acc + cur.y, 0)) + ' €'}
							/>
							<NumberFrame
								label="Totale spesa residua"
								value={formatToDecimals(yearDataPoints.residual?.reduce((acc, cur) => acc + cur.y, 0)) + ' €'}
							/>
							<NumberFrame
								label="Totale incasso previsto"
								value={formatToDecimals(yearDataPoints.cashedAmount?.reduce((acc, cur) => acc + cur.y, 0)) + ' €'}
							/>
							<NumberFrame
								label="Totale incasso effettivo"
								value={formatToDecimals(cashedInData?.reduce((acc, cur) => acc + cur.cashedInAmount, 0)) + ' €'}
							/>
						</div>
						<div style={{ width: 300, display: 'flex', gap: 10, alignItems: 'center' }}>
							<Select
								className="react-select-container"
								classNamePrefix="react-select"
								placeholder="Seleziona anno"
								options={yearOptions}
								onChange={(selectedOpt) => setSelectedYear(Number(selectedOpt.value))}
								value={selectedYear ? { value: selectedYear?.toString(), label: selectedYear?.toString() } : null}
							/>
							{selectedYear && (
								<Button variant="info" onClick={() => setSelectedYear(undefined)}>
									Indietro
								</Button>
							)}
						</div>
						<LineChart
							data={selectedYear ? monthDataPoints : yearDataPoints}
							onDataPointClick={setSelectedYear}
							isFocused={!!selectedYear}
						/>
						<OESimpleTable
							columnDefs={columnDefsTotalsByYears}
							rowData={processTotalsByYears(totalsByYears, cashedInData)}
							bottomColumns={[{ year: 'TOTALE' }, 'amount', 'finalAmount', 'accumulatedResidual', 'cashedAmount', 'cashedIn']}
							sortable
							pinnedBottomRow
							fullWidth
						/>
					</SectionFrame>
				</Col>
			</Row>
			<Row className="mt-5 mb-4">
				<Col>
					<SectionFrame>
						<div className="row">
							<SectionTitle>Voci spesa</SectionTitle>
						</div>
						<div className="row gap-4">
							<NumberFrame
								label="Totale spesa prevista"
								value={formatToDecimals(totalsByExpenseItems?.reduce((acc, cur) => acc + cur.amount, 0)) + ' €'}
							/>
							<NumberFrame
								label="Totale spesa effettuata"
								value={formatToDecimals(totalsByExpenseItems?.reduce((acc, cur) => acc + cur.finalAmount, 0)) + ' €'}
							/>
							<NumberFrame
								label="Totale spesa residua"
								value={formatToDecimals(totalsByExpenseItems?.reduce((acc, cur) => acc + cur.residual, 0)) + ' €'}
							/>
						</div>
						<div className="row gap-4">
							{totalsByExpenseItems?.map((item) => (
								<ExpenseItemCard
									key={item.voceDiSpesa + item.sottovoceDiSpesa}
									title={item.voceDiSpesa}
									subtitle={item.sottovoceDiSpesa}
									residual={item.residual}
									graph={<ExpenseItemHistogram amount={item.amount} finalAmount={item.finalAmount} />}
								/>
							))}
						</div>
						{/* <OESimpleTable
							columnDefs={columnDefsTotalsByExpenseItems}
							rowData={totalsByExpenseItems}
							pageSizeId={'financial-totals-expense-items-table'}
							sortable={true}
							pinnedBottomRow={true}
							bottomColumns={[{ voceDiSpesa: 'TOTALE' }, 'amount', 'finalAmount', 'residual']}
						/> */}
					</SectionFrame>
				</Col>
			</Row>
		</>
	);
};
export default FinancialTotalsTab;
